/*
 * VENDORED from the nn-favorites app (extensions/list-blocks/assets/header-icon.js).
 * Only the <nn-favorites-header-icon> custom element is included — the app file's
 * HeaderIconManager (clone/placement logic for hidden app-embed icons) is omitted
 * because the theme renders the icon directly in snippets/header-actions.liquid,
 * so nothing needs repositioning. Wrapped in a guarded IIFE so it can coexist with
 * the app's own blocks/embeds if they are enabled later. Do not hand-edit —
 * re-sync from the app when it updates.
 * Loaded by snippets/nn-favorites-header-icon.liquid.
 */
(function () {
  if (customElements.get("nn-favorites-header-icon")) return;

  /**
   * Displays a header icon with badge showing favorites count.
   * Initializes global state from server-rendered collection data.
   */
  class NnFavoritesHeaderIcon extends HTMLElement {
    /** @type {AbortController|null} */
    #ac = null;

    connectedCallback() {
      this.#ac = new AbortController();
      const { signal } = this.#ac;

      if (window.NnFavoritesState) {
        this.#boot(signal);
      } else {
        document.addEventListener(
          "nn-favorites:ready",
          () => this.#boot(signal),
          { once: true, signal },
        );
      }
    }

    #boot() {
      if (!this.dataset.cloneOf && this.dataset.collectionJson) {
        try {
          window.NnFavoritesState.setProducts(
            this.dataset.listType,
            JSON.parse(this.dataset.collectionJson),
            this.dataset.productIds?.split(",") ?? [],
          );
        } catch {
          /* ignore invalid JSON */
        }
      }

      window.NnFavoritesState.addEventListener(
        "change",
        (e) => this.#updateUI(/** @type {CustomEvent} */ (e)),
        { signal: this.#ac.signal },
      );
      this.#updateUI();
    }

    disconnectedCallback() {
      this.#ac?.abort();
    }

    /** @param {CustomEvent} [event] */
    #updateUI(event) {
      const listType = this.dataset.listType;
      if (event && event.detail?.listType !== listType) return;

      const count = window.NnFavoritesState.getProducts(listType).size;
      const handle = window.NnFavoritesState.getCollectionHandle(listType);
      const isTextVariant = this.dataset.variant === "text";

      /** @type {HTMLElement|null} */
      const countEl = this.querySelector('[ref="badgeCount"]');
      if (countEl) {
        if (isTextVariant) {
          // Text variant: show actual count
          countEl.textContent = String(count);
        } else {
          // Icon variant: show count only if 1-99
          countEl.textContent = count > 0 && count < 100 ? String(count) : "";
        }
      }

      // Icon variant: toggle badge visibility
      this.querySelector('[ref="badge"]')?.classList.toggle(
        "visually-hidden",
        count === 0,
      );

      // Link variant: toggle count visibility
      this.querySelector('[ref="linkCount"]')?.classList.toggle(
        "visually-hidden",
        count === 0,
      );

      this.classList.toggle("nn-favorites-header-icon--has-items", count > 0);
      this.classList.toggle(
        "nn-favorites-header-icon--empty-count",
        count === 0 || count >= 100,
      );

      /** @type {HTMLAnchorElement|null} */
      const link = this.querySelector('[ref="link"]');
      if (handle && link) {
        const collectionUrl = `/collections/${handle}`;
        if (link.getAttribute("href") !== collectionUrl) {
          link.setAttribute("href", collectionUrl);
        }
      }
    }
  }

  customElements.define("nn-favorites-header-icon", NnFavoritesHeaderIcon);
})();
